/**
 * Lightweight localStorage draft persistence for inspection item edits.
 * Saves message/status changes immediately so they survive page reloads
 * and disconnections.
 *
 * Drafts auto-expire after 7 days to prevent stale data.
 */

const PREFIX = "chariot-draft:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStorageAvailable() {
  try {
    const k = "__test__";
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Save a draft value under a key.
 * Entries include a timestamp for expiry checking.
 */
export function saveDraft(key, value) {
  if (!isStorageAvailable()) return;
  try {
    const entry = { value, ts: Date.now() };
    localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch (e) {
    // Quota exceeded or similar — silently fail
  }
}

/**
 * Get a draft value. Returns null if not found or expired.
 */
export function getDraft(key) {
  if (!isStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry.ts || Date.now() - entry.ts > MAX_AGE_MS) {
      localStorage.removeItem(PREFIX + key);
      return null;
    }
    return entry.value;
  } catch (e) {
    return null;
  }
}

/**
 * Clear a draft — call after successful API save.
 */
export function clearDraft(key) {
  if (!isStorageAvailable()) return;
  try {
    localStorage.removeItem(PREFIX + key);
  } catch (e) { /* ignore */ }
}

/** Clear every draft whose key starts with `prefix`. */
export function clearDraftsStartingWith(prefix) {
  if (!prefix || !isStorageAvailable()) return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX + prefix)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* ignore */ }
}

// What a technician typed for "Write it up" (components/OrderDetail/work/ScribePanel.js):
// one per person, order and service — never shown to the next person on a
// shared iPad, and gone when they sign out (AuthContext).
export const scribeDraftKey = (userId, orderId, serviceId) => `scribe-${userId}-${orderId}-${serviceId}`;
export const forgetScribeDrafts = (userId) => { if (userId) clearDraftsStartingWith(`scribe-${userId}-`); };

/**
 * Purge all expired drafts. Run occasionally (e.g. on app mount).
 */
export function purgeExpiredDrafts() {
  if (!isStorageAvailable()) return;
  try {
    const now = Date.now();
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      try {
        const entry = JSON.parse(localStorage.getItem(k));
        if (!entry.ts || now - entry.ts > MAX_AGE_MS) {
          localStorage.removeItem(k);
        }
      } catch (e) {
        localStorage.removeItem(k);
      }
    }
  } catch (e) { /* ignore */ }
}
